import { useEffect, useRef, useState } from 'react';
import { ArrowDownAZ, Check } from 'lucide-react';

export interface SortOption {
  key: string;            // "<field>:<dir>"
  field: string;
  dir: 'asc' | 'desc';
  label: string;
}

/** Sort options for a SMART list, per entity kind (mirrors backend SORTABLE in listFilter.ts). */
export function smartSortOptions(entityKind: string): SortOption[] {
  if (entityKind === 'PERSON') {
    return [
      { key: 'lastName:asc', field: 'lastName', dir: 'asc', label: 'Name A→Z' },
      { key: 'lastName:desc', field: 'lastName', dir: 'desc', label: 'Name Z→A' },
      { key: 'lastActivityAt:desc', field: 'lastActivityAt', dir: 'desc', label: 'Last activity (newest)' },
    ];
  }
  if (entityKind === 'PROGRAM') {
    return [
      { key: 'level:asc', field: 'level', dir: 'asc', label: 'Level A→Z' },
      { key: 'rosterCount:desc', field: 'rosterCount', dir: 'desc', label: 'Roster size (largest)' },
    ];
  }
  // ORG / SCHOOL / CLUB_TEAM
  return [
    { key: 'name:asc', field: 'name', dir: 'asc', label: 'Name A→Z' },
    { key: 'name:desc', field: 'name', dir: 'desc', label: 'Name Z→A' },
    { key: 'ncesEnrollment:desc', field: 'ncesEnrollment', dir: 'desc', label: 'Enrollment (largest)' },
    { key: 'lastActivityAt:desc', field: 'lastActivityAt', dir: 'desc', label: 'Last activity (newest)' },
    { key: 'lastVisitAt:desc', field: 'lastVisitAt', dir: 'desc', label: 'Last visit (newest)' },
  ];
}

/** Sort options for a MANUAL list (mirrors backend sortItemsSchema). */
export const MANUAL_SORT_OPTIONS: SortOption[] = [
  { key: 'name:asc', field: 'name', dir: 'asc', label: 'Name A→Z' },
  { key: 'name:desc', field: 'name', dir: 'desc', label: 'Name Z→A' },
  { key: 'lastActivityAt:desc', field: 'lastActivityAt', dir: 'desc', label: 'Last activity (newest)' },
  { key: 'dateAdded:asc', field: 'dateAdded', dir: 'asc', label: 'Date added (oldest)' },
  { key: 'dateAdded:desc', field: 'dateAdded', dir: 'desc', label: 'Date added (newest)' },
];

interface Props {
  options: SortOption[];
  /** Currently-saved sort ("<field>:<dir>") — smart lists only; manual passes null. */
  currentKey: string | null;
  onPick: (opt: SortOption) => void;
  disabled?: boolean;
}

export function ListSortMenu({ options, currentKey, onPick, disabled }: Props) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close on outside click (same pattern as AddToListSheet).
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        className="btn-secondary disabled:opacity-50"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <ArrowDownAZ className="h-4 w-4 mr-1.5" />
        Sort
      </button>
      {open && (
        <div role="menu" className="absolute left-0 z-30 mt-1 card w-60 overflow-hidden shadow-xl">
          {options.map((opt) => {
            const active = currentKey === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                role="menuitem"
                onClick={() => { setOpen(false); onPick(opt); }}
                className="flex w-full items-center justify-between gap-2 border-b border-gray-100 dark:border-gray-700/60 px-3 py-2.5 text-left text-sm text-gray-700 dark:text-gray-200 transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/30 last:border-b-0"
              >
                <span>{opt.label}</span>
                {active && <Check className="h-4 w-4 flex-none text-lime-600 dark:text-lime-400" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
